// ============================================================================
// ROLE PERMISSION HOOKS
// ============================================================================
// Mirrors the prima-web/src/hooks/api/useRolePermissions.ts pattern:
//   - useRolePermissions(params)         → paginated list (admin screen)
//   - useRolePermissionsByRoles(roles)   → merged map for the logged-in user
//   - useCreateRolePermission / useUpdateRolePermission / useDeleteRolePermission

import { useCallback, useEffect, useMemo, useState } from 'react';
import { rolePermissionService } from 'api/services';
import type {
  RolePermission,
  RolePermissionMerged,
  RolePermissionSearchRequest,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
  PaginatedResponse,
  ID,
} from 'api/types';

const EMPTY_PAGINATION: PaginatedResponse<RolePermission>['pagination'] = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Paginated list — used by RolePermissionsScreen.
 * Refetches whenever `params` changes (compared via JSON-stable key).
 */
export const useRolePermissions = (params?: RolePermissionSearchRequest) => {
  const [data, setData] = useState<RolePermission[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<RolePermission>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchRolePermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchRolePermissions();
  }, [fetchRolePermissions]);

  return { data, pagination, loading, error, refetch: fetchRolePermissions };
};

/**
 * Consumer-side: returns the merged visible_modules + module_actions for the
 * supplied roles. Called at login by AuthContext (which uses the service
 * directly), and available for any component that wants role-resolved
 * permissions reactively.
 */
export const useRolePermissionsByRoles = (roles: ReadonlyArray<string> | null | undefined) => {
  const [data, setData] = useState<RolePermissionMerged | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Stable serialisation to avoid effect thrash when callers pass fresh array literals.
  const rolesKey = (roles ?? []).filter((r) => !!r).join(',');

  const fetchPermissions = useCallback(async () => {
    const roleList = rolesKey.split(',').filter((r) => r.length > 0);
    if (roleList.length === 0) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.getByRoles(roleList);
      setData(response.success && response.data ? response.data : null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load role permissions'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [rolesKey]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return { data, loading, error, refetch: fetchPermissions };
};

/**
 * Single row by id.
 */
export const useRolePermission = (id: ID | null | undefined) => {
  const [data, setData] = useState<RolePermission | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.getById(id);
      setData(response.success && response.data ? response.data : null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load role permission'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

// ----------------------------------------------------------------------------
// CRUD MUTATIONS — prima options-callback style
// ----------------------------------------------------------------------------

export const useCreateRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createRolePermission = async (
    payload: CreateRolePermissionRequest,
    options?: {
      onSuccess?: (item: RolePermission) => void;
      onError?: (error: Error) => void;
    },
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.create(payload);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createRolePermission, loading, error };
};

export const useUpdateRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateRolePermission = async (
    id: ID,
    payload: UpdateRolePermissionRequest,
    options?: {
      onSuccess?: (item: RolePermission) => void;
      onError?: (error: Error) => void;
    },
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.update(id, payload);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateRolePermission, loading, error };
};

/**
 * Delete takes `(id, rowVersion)` — CRS API requires the row version for
 * optimistic-concurrency soft delete (see RolePermissionsController.Delete).
 * This is the one signature difference from prima, where the API does the
 * rowVersion lookup server-side.
 */
export const useDeleteRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteRolePermission = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    },
  ) => {
    setLoading(true);
    setError(null);
    try {
      await rolePermissionService.delete(id, rowVersion);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteRolePermission, loading, error };
};
